import { useEffect } from "react";
import { NavLink } from "react-router-dom";
import { UserButton } from "@clerk/react";
import { api } from "../lib/api";
import { TierBadge } from "./common";
import { LayoutDashboard, Upload, Search, Users, Network, Inbox, BadgeDollarSign, PlugZap, ClipboardCheck, Settings, ScrollText, Gauge, Send, Layers } from "lucide-react";

const NAV = {
  admin: [["/", "Dashboard", LayoutDashboard], ["/uploads", "Lead uploads", Upload], ["/leads", "Master search", Search],
    ["/teams", "Teams", Network], ["/users", "Users & allowances", Users], ["/requests", "Requests & rotation", Inbox], ["/batches", "Rep batches", Layers],
    ["/funded", "Funded deals", BadgeDollarSign], ["/entrance", "Entrance", PlugZap], ["/my-entrance", "My Entrance (test)", Send], ["/review", "Import review", ClipboardCheck],
    ["/settings", "Settings", Settings], ["/audit", "Audit log", ScrollText]],
  leader: [["/", "Team dashboard", LayoutDashboard], ["/mine", "My leads", Gauge], ["/my-entrance", "My Entrance", PlugZap], ["/leads", "Search", Search], ["/funded", "Funded deals", BadgeDollarSign]],
  rep: [["/", "My dashboard", LayoutDashboard], ["/my-entrance", "My Entrance", PlugZap], ["/leads", "Search", Search], ["/funded", "Funded deals", BadgeDollarSign]],
};

export default function Layout({ me, children }) {
  const nav = NAV[me.role].filter(([p]) => p !== "/mine" || me.personal_leads);
  useEffect(() => {
    if (me.role === "admin" || sessionStorage.getItem(`entrance-check-${me.id}`)) { return; }
    sessionStorage.setItem(`entrance-check-${me.id}`, "1");
    api.post("/my-entrance/check").catch(() => {});
  }, [me.id, me.role]);
  return (
    <div className="min-h-screen flex flex-col md:flex-row canvas">
      <aside className="md:w-64 md:h-screen md:sticky md:top-0 bg-white border-b md:border-b-0 md:border-r border-zinc-200 text-zinc-700 flex md:flex-col shrink-0" data-testid="sidebar">
        <div className="px-5 pt-6 pb-5 hidden md:block">
          <img src="/logo-full.png" alt="Choicelead.ai" className="h-7 w-auto object-contain" data-testid="sidebar-logo" />
          <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-zinc-50 px-2.5 py-1">
            <span className="live-dot" /><span className="eyebrow !text-zinc-600">{me.role === "leader" ? "Team leader" : me.role}</span>
          </div>
        </div>
        <img src="/c-mark.png" alt="Choicelead.ai" className="md:hidden h-8 w-8 m-2 self-center object-contain" />
        <nav className="flex md:flex-col overflow-x-auto md:overflow-y-auto flex-1 p-2 md:px-3 gap-1">
          {nav.map(([to, label, Icon]) => (
            <NavLink key={to} to={to} end={to === "/"} data-testid={`nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) => `group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-[background-color,color,box-shadow] duration-150 ${isActive ? "bg-zinc-950 text-white shadow-[0_6px_16px_-8px_rgba(10,10,11,0.6)]" : "hover:bg-zinc-100 hover:text-zinc-950"}`}>
              {({ isActive }) => <><Icon className={`w-4 h-4 transition-colors ${isActive ? "text-[#7fd12b]" : "text-zinc-400 group-hover:text-[#58a80a]"}`} />{label}</>}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 md:p-4 md:m-3 md:rounded-xl md:border md:border-zinc-200 md:bg-zinc-50 flex items-center gap-2">
          <UserButton />
          <div className="min-w-0 hidden md:block"><div className="text-xs font-semibold text-zinc-800 truncate" data-testid="current-user-name">{me.display_name}</div>
            {me.role !== "admin" && <div className="mt-1"><TierBadge tier={me.tier} size="sm" testid="sidebar-tier-badge" /></div>}</div>
        </div>
      </aside>
      <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-10">{children}</main>
    </div>
  );
}
